import { Page } from "@/types";
import { useContext } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Timeline } from "primereact/timeline";
import { Chip } from "primereact/chip";
import Layout from '../layout/layout';
import AppBar from '../layout/AppBar';
import { LayoutContext } from "../../admin/layout/context/layoutcontext";
import { 
  FaGlobe,
  FaUsers,
  FaBullseye,
  FaLightbulb,
  FaShieldAlt,
  FaChartLine,
  FaWater,
  FaLeaf,
  FaAward,
  FaHandshake,
  FaRocket,
  FaHeart,
  FaCompass,
  FaBalanceScale,
  FaBrain,
  FaCloud,
  FaMobileAlt,
  FaDatabase,
  FaRobot,
  FaMapMarkedAlt
} from "react-icons/fa";
import { GiWaterDrop, GiTreeGrowth, GiPlanetCore } from "react-icons/gi";


const AboutUs: Page = () => {
    const { layoutConfig, colors } = useContext(LayoutContext);

    // Company milestones
    const milestones = [
        {
            year: '2018',
            title: 'Foundation',
            description: 'AmanziLytics was founded by water engineers and data scientists passionate about solving water scarcity.',
            icon: '🌱'
        },
        {
            year: '2019',
            title: 'First Deployment',
            description: 'Successfully deployed our platform for a municipal water utility, reducing water loss by 35%.',
            icon: '🚀'
        },
        {
            year: '2020',
            title: 'Series A Funding',
            description: 'Raised $8M to expand our AI capabilities and geographic reach.',
            icon: '💰'
        },
        {
            year: '2021',
            title: 'Global Expansion',
            description: 'Expanded to serve water utilities across 3 continents with 50+ municipal partners.',
            icon: '🌍'
        },
        {
            year: '2022',
            title: 'AI Innovation Award',
            description: 'Received prestigious award for innovation in sustainable water management.',
            icon: '🏆'
        },
        {
            year: '2023',
            title: 'Platform v3.0',
            description: 'Launched next-gen platform with predictive analytics and real-time monitoring.',
            icon: '✨'
        },
        {
            year: '2024',
            title: 'Current',
            description: 'Serving 100+ cities worldwide, preventing billions of liters of water waste annually.',
            icon: '📈'
        },
    ];

    // Core values
    const values = [
        {
            icon: <FaHeart />,
            title: 'Sustainability',
            description: 'We believe in technology that protects our planet for future generations.',
            color: colors.successColor
        },
        {
            icon: <FaCompass />,
            title: 'Innovation',
            description: 'Continuously pushing boundaries to solve complex water challenges.',
            color: colors.primaryText
        },
        {
            icon: <FaUsers />,
            title: 'Collaboration',
            description: 'Working alongside communities and experts to create meaningful impact.',
            color: colors.infoColor
        },
        {
            icon: <FaBalanceScale />,
            title: 'Integrity',
            description: 'Transparent, ethical practices in everything we do.',
            color: colors.warningColor
        },
    ];

    // Leadership team
    const leadershipTeam = [
        {
            name: 'Dr. Sarah Chen',
            role: 'CEO & Co-Founder',
            bio: 'Former water resources engineer with 15+ years experience in municipal water management. PhD in Environmental Engineering.',
            expertise: ['Water Systems', 'AI/ML', 'Sustainability'],
            image: '👩‍🔬',
            linkedin: '#'
        },
        {
            name: 'Michael Rodriguez',
            role: 'CTO',
            bio: 'Former Google AI researcher specializing in predictive analytics for environmental systems.',
            expertise: ['Machine Learning', 'Big Data', 'Cloud Architecture'],
            image: '👨‍💻',
            linkedin: '#'
        },
        {
            name: 'Dr. Aisha Williams',
            role: 'Chief Scientist',
            bio: 'Renowned hydrologist with publications in Nature Water. Former UN water consultant.',
            expertise: ['Hydrology', 'Climate Science', 'Policy'],
            image: '👩‍🏫',
            linkedin: '#'
        },
        {
            name: 'David Kim',
            role: 'Head of Product',
            bio: 'Product leader from Tesla Energy, passionate about creating intuitive user experiences.',
            expertise: ['UX Design', 'Product Strategy', 'User Research'],
            image: '👨‍💼',
            linkedin: '#'
        },
    ];

    // Technology pillars
    const techPillars = [
        {
            icon: <FaBrain />,
            title: 'AI & Machine Learning',
            description: 'Predictive models that anticipate water demand, detect leaks, and optimize distribution.',
            features: ['Anomaly Detection', 'Demand Forecasting', 'Predictive Maintenance']
        },
        {
            icon: <FaCloud />,
            title: 'Cloud Infrastructure',
            description: 'Scalable, secure cloud platform for real-time data processing and analytics.',
            features: ['Real-time Processing', 'Secure Data Storage', 'Global Availability']
        },
        {
            icon: <FaMobileAlt />,
            title: 'IoT Integration',
            description: 'Connect and monitor sensors across water infrastructure for comprehensive visibility.',
            features: ['Sensor Networks', 'Remote Monitoring', 'Edge Computing']
        },
        {
            icon: <FaDatabase />,
            title: 'Data Analytics',
            description: 'Advanced analytics platform transforming raw data into actionable insights.',
            features: ['Data Visualization', 'Trend Analysis', 'Custom Reporting']
        },
    ];

    // Partners and recognition
    const partners = [
        { name: 'UN Water', logo: '💧', type: 'Strategic Partner' },
        { name: 'World Bank', logo: '🏦', type: 'Funding Partner' },
        { name: 'MIT Water', logo: '🎓', type: 'Research Partner' },
        { name: 'AWS', logo: '☁️', type: 'Technology Partner' },
        { name: 'Siemens', logo: '⚡', type: 'Infrastructure Partner' },
        { name: 'Water.org', logo: '🤝', type: 'Impact Partner' },
    ];

    const awards = [
        { name: 'Tech for Good Award 2023', issuer: 'World Economic Forum' },
        { name: 'Green Tech Innovation', issuer: 'UN Environment Programme' },
        { name: 'Best AI Solution', issuer: 'TechCrunch Disrupt' },
        { name: 'Sustainability Excellence', issuer: 'Forbes' },
    ];

    const milestonesTemplate = (item: any) => {
        return (
            <div className="flex flex-column md:flex-row gap-4 p-4 border-round-xl hover:shadow-3 transition-all duration-300 cursor-pointer"
                 style={{ 
                     backgroundColor: colors.hoverBg,
                     border: `1px solid ${colors.borderColor}`
                 }}>
                <div className="flex-shrink-0">
                    <div className="w-16 h-16 border-round-lg flex align-items-center justify-content-center text-2xl"
                         style={{ 
                             backgroundColor: colors.primaryBg,
                             color: colors.primaryText 
                         }}>
                        {item.icon}
                    </div>
                </div>
                <div className="flex-1">
                    <div className="flex align-items-center gap-2 mb-2">
                        <span className="font-bold text-xl" style={{ color: colors.primaryText }}>
                            {item.year}
                        </span>
                        <span className="font-bold text-lg" style={{ color: colors.textColor }}>
                            {item.title}
                        </span>
                    </div>
                    <p className="m-0" style={{ color: colors.lightText }}>
                        {item.description}
                    </p>
                </div>
            </div>
        );
    };

    return (
        <Layout>
            <div className="relative flex flex-column min-h-screen">
                {/* Background elements */}
                <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
                    <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>
                    <div className="absolute top-60 -right-40 w-80 h-80 bg-cyan-500/5 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-40 left-1/2 -translate-x-1/2 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl"></div>
                </div>

                {/* AppBar */}
                <AppBar scrolled={false} navItems={[
                    { id: 'about-us', label: 'About Us' },
                    { id: 'contact', label: 'Contact'},
                ]} />

                {/* Main Content */}
                <div className="flex-grow-1 pt-16">
                    <div className="py-12 px-4 mx-0 md:mx-6 lg:mx-8 lg:px-8">
                        {/* Hero Section */}
                        <div className="grid grid-nogutter justify-content-between align-items-center mb-16 py-8">
                            <div className="col-12 md:col-7 flex flex-column gap-4">
                                <div className="flex align-items-center gap-2">
                                    <span className="font-bold px-3 py-1 border-round text-sm"
                                          style={{ 
                                              backgroundColor: colors.primaryBg, 
                                              color: colors.primaryText 
                                          }}>
                                        ABOUT AMANZILYTICS
                                    </span>
                                    <span className="text-sm font-medium" style={{ color: colors.lightText }}>
                                        Transforming Water Management Since 2018
                                    </span>
                                </div>
                                <h1 className="font-bold text-4xl md:text-5xl lg:text-6xl line-height-2" 
                                    style={{ color: colors.textColor }}>
                                    Intelligent Water.<br />Sustainable Future.
                                </h1>
                                <p className="text-lg line-height-3 max-w-30rem" style={{ color: colors.lightText }}>
                                    We're on a mission to revolutionize water management through AI-powered intelligence. 
                                    Our platform helps cities and utilities optimize water usage, predict shortages, 
                                    and build resilient water systems for generations to come.
                                </p>
                                <div className="flex flex-wrap gap-4 mt-2">
                                    <div className="flex align-items-center gap-2">
                                        <div className="w-2 h-2 border-round" style={{ backgroundColor: colors.successColor }}></div>
                                        <span className="text-sm font-medium" style={{ color: colors.lightText }}>
                                            100+ Cities Served
                                        </span>
                                    </div>
                                    <div className="flex align-items-center gap-2">
                                        <div className="w-2 h-2 border-round" style={{ backgroundColor: colors.infoColor }}></div>
                                        <span className="text-sm font-medium" style={{ color: colors.lightText }}>
                                            5+ Billion Liters Saved
                                        </span>
                                    </div>
                                    <div className="flex align-items-center gap-2">
                                        <div className="w-2 h-2 border-round" style={{ backgroundColor: colors.warningColor }}></div>
                                        <span className="text-sm font-medium" style={{ color: colors.lightText }}>
                                            Global Recognition
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 md:col-5 mt-6 md:mt-0">
                                <div className="p-5 border-round-2xl shadow-3"
                                     style={{ 
                                         backgroundColor: colors.cardBg, 
                                         border: `1px solid ${colors.borderColor}`,
                                         background: `linear-gradient(145deg, ${colors.cardBg}, ${colors.surfaceBg})`
                                     }}>
                                    <div className="flex align-items-center gap-3 mb-4">
                                        <div className="p-3 border-round-lg" 
                                             style={{ 
                                                 backgroundColor: colors.primaryBg,
                                                 color: colors.primaryText 
                                             }}>
                                            <GiWaterDrop size={24} />
                                        </div>
                                        <h3 className="font-semibold text-lg m-0" style={{ color: colors.textColor }}>
                                            Our Impact in Numbers
                                        </h3>
                                    </div>
                                    <div className="grid">
                                        <div className="col-6 text-center py-4">
                                            <div className="text-4xl font-bold mb-1" style={{ color: colors.primaryText }}>
                                                100+
                                            </div>
                                            <div className="text-sm" style={{ color: colors.lightText }}>
                                                Municipal Partners
                                            </div>
                                        </div>
                                        <div className="col-6 text-center py-4">
                                            <div className="text-4xl font-bold mb-1" style={{ color: colors.successColor }}>
                                                35%
                                            </div>
                                            <div className="text-sm" style={{ color: colors.lightText }}>
                                                Avg. Water Loss Reduction
                                            </div>
                                        </div>
                                        <div className="col-6 text-center py-4">
                                            <div className="text-4xl font-bold mb-1" style={{ color: colors.infoColor }}>
                                                24/7
                                            </div>
                                            <div className="text-sm" style={{ color: colors.lightText }}>
                                                Real-time Monitoring
                                            </div>
                                        </div>
                                        <div className="col-6 text-center py-4">
                                            <div className="text-4xl font-bold mb-1" style={{ color: colors.warningColor }}>
                                                50+
                                            </div>
                                            <div className="text-sm" style={{ color: colors.lightText }}>
                                                Countries Served
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mission Section */}
                        <div className="mb-16">
                            <div className="text-center mb-8">
                                <span className="font-bold px-4 py-2 border-round-full text-sm inline-block mb-4"
                                      style={{ 
                                          backgroundColor: colors.primaryBg, 
                                          color: colors.primaryText 
                                      }}>
                                    OUR PURPOSE
                                </span>
                                <h2 className="font-bold text-3xl mb-4" style={{ color: colors.textColor }}>
                                    More Than Technology — A Movement
                                </h2>
                                <p className="text-xl max-w-3xl mx-auto" style={{ color: colors.lightText }}>
                                    We believe that intelligent water management is the cornerstone of sustainable 
                                    communities and a healthy planet.
                                </p>
                            </div>
                            <div className="grid">
                                <div className="col-12 md:col-4 p-2">
                                    <Card className="h-full border-round-xl shadow-2"
                                          style={{ 
                                              backgroundColor: colors.cardBg, 
                                              borderColor: colors.borderColor,
                                              borderTop: `4px solid ${colors.primaryText}`
                                          }}>
                                        <div className="text-center py-6">
                                            <div className="p-4 border-round-lg inline-block mb-4"
                                                 style={{ 
                                                     backgroundColor: colors.primaryBg,
                                                     color: colors.primaryText 
                                                 }}>
                                                <FaBullseye size={32} />
                                            </div>
                                            <h3 className="font-bold text-xl mb-3" style={{ color: colors.textColor }}>
                                                Our Mission
                                            </h3>
                                            <p style={{ color: colors.lightText }}>
                                                Empower every community with intelligent water management solutions 
                                                that ensure sustainability, resilience, and equitable access for all.
                                            </p>
                                        </div>
                                    </Card>
                                </div>
                                <div className="col-12 md:col-4 p-2">
                                    <Card className="h-full border-round-xl shadow-2"
                                          style={{ 
                                              backgroundColor: colors.cardBg, 
                                              borderColor: colors.borderColor,
                                              borderTop: `4px solid ${colors.successColor}`
                                          }}>
                                        <div className="text-center py-6">
                                            <div className="p-4 border-round-lg inline-block mb-4"
                                                 style={{ 
                                                     backgroundColor: `${colors.successColor}20`,
                                                     color: colors.successColor 
                                                 }}>
                                                <FaGlobe size={32} />
                                            </div>
                                            <h3 className="font-bold text-xl mb-3" style={{ color: colors.textColor }}>
                                                Our Vision
                                            </h3>
                                            <p style={{ color: colors.lightText }}>
                                                A world where every drop of water is valued, conserved, and managed 
                                                intelligently through technology and innovation.
                                            </p>
                                        </div>
                                    </Card>
                                </div>
                                <div className="col-12 md:col-4 p-2">
                                    <Card className="h-full border-round-xl shadow-2"
                                          style={{ 
                                              backgroundColor: colors.cardBg, 
                                              borderColor: colors.borderColor,
                                              borderTop: `4px solid ${colors.infoColor}`
                                          }}>
                                        <div className="text-center py-6">
                                            <div className="p-4 border-round-lg inline-block mb-4"
                                                 style={{ 
                                                     backgroundColor: `${colors.infoColor}20`,
                                                     color: colors.infoColor 
                                                 }}>
                                                <GiPlanetCore size={32} />
                                            </div>
                                            <h3 className="font-bold text-xl mb-3" style={{ color: colors.textColor }}>
                                                Our Promise
                                            </h3>
                                            <p style={{ color: colors.lightText }}>
                                                To deliver cutting-edge solutions that make water management 
                                                smarter, more efficient, and sustainable for generations to come.
                                            </p>
                                        </div>
                                    </Card>
                                </div>
                            </div>
                        </div>

                        {/* Values Section */}
                        <div className="mb-16">
                            <div className="text-center mb-8">
                                <h2 className="font-bold text-3xl mb-4" style={{ color: colors.textColor }}>
                                    Our Core Values
                                </h2>
                                <p className="text-xl max-w-3xl mx-auto" style={{ color: colors.lightText }}>
                                    These principles guide everything we do, from product development to customer partnerships.
                                </p>
                            </div>
                            <div className="grid">
                                {values.map((value, index) => (
                                    <div key={index} className="col-12 md:col-6 p-2">
                                        <div className="p-5 border-round-xl shadow-1 hover:shadow-3 transition-all duration-300"
                                             style={{ 
                                                 backgroundColor: colors.cardBg, 
                                                 border: `1px solid ${colors.borderColor}`
                                             }}>
                                            <div className="flex align-items-center gap-4 mb-4">
                                                <div className="p-3 border-round-lg"
                                                     style={{ 
                                                         backgroundColor: `${value.color}20`,
                                                         color: value.color 
                                                     }}>
                                                    {value.icon}
                                                </div>
                                                <h3 className="font-bold text-lg m-0" style={{ color: colors.textColor }}>
                                                    {value.title}
                                                </h3>
                                            </div>
                                            <p className="m-0" style={{ color: colors.lightText }}>
                                                {value.description}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Technology Section */}
                        <div className="mb-16">
                            <div className="text-center mb-8">
                                <span className="font-bold px-4 py-2 border-round-full text-sm inline-block mb-4"
                                      style={{ 
                                          backgroundColor: colors.primaryBg, 
                                          color: colors.primaryText 
                                      }}>
                                    OUR TECHNOLOGY
                                </span>
                                <h2 className="font-bold text-3xl mb-4" style={{ color: colors.textColor }}>
                                    Powered by Innovation
                                </h2>
                                <p className="text-xl max-w-3xl mx-auto" style={{ color: colors.lightText }}>
                                    We combine cutting-edge technologies to create comprehensive water intelligence solutions.
                                </p>
                            </div>
                            <div className="grid">
                                {techPillars.map((pillar, index) => (
                                    <div key={index} className="col-12 md:col-3 p-2">
                                        <Card className="h-full border-round-xl shadow-2 hover:shadow-4 transition-all duration-300"
                                              style={{ 
                                                  backgroundColor: colors.cardBg, 
                                                  borderColor: colors.borderColor 
                                              }}>
                                            <div className="text-center mb-4">
                                                <div className="p-3 border-round-lg inline-block mb-3"
                                                     style={{ 
                                                         backgroundColor: colors.primaryBg,
                                                         color: colors.primaryText 
                                                     }}>
                                                    {pillar.icon}
                                                </div>
                                                <h3 className="font-bold text-lg mb-3" style={{ color: colors.textColor }}>
                                                    {pillar.title}
                                                </h3>
                                                <p className="text-sm mb-4" style={{ color: colors.lightText }}>
                                                    {pillar.description}
                                                </p>
                                            </div>
                                            <div className="flex flex-wrap gap-2 justify-content-center">
                                                {pillar.features.map((feature, idx) => (
                                                    <Chip 
                                                        key={idx}
                                                        label={feature}
                                                        className="text-xs"
                                                        style={{ 
                                                            backgroundColor: colors.surfaceBg,
                                                            color: colors.lightText
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                        </Card>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default AboutUs;